



















































































/* General */
import Vue from 'vue'

/* Mixins */

/* Components */
import Draggable from 'vuedraggable'

/* Helpers */
import RouteAssistant from '@/helpers/route-assistant'

/* Data types */
import {
  IHomeKanbanColumn,
  ITaskForKanbanWithManualOrder,
} from '@/types/data-objects/items'

/* API */
import {
  IKanbanColumnOrdering,
  IPersonBase,
  IPriorityLevelForSelection,
  IStatusForSelection,
  ITaskForKanban,
  IWorkflowDetailStatusTransition,
} from '@/api/types/response.types'

export default Vue.extend({
  name: 'KanbanBoard',

  components: {
    Draggable,
  },

  props: {
    isDisabled: {
      type: Boolean,
      default: false,
    },
    entityId: {
      type: Number,
      required: true,
    },
    tasks: {
      type: Array as () => ITaskForKanbanWithManualOrder[],
      default: null,
    },
    columnOrderings: {
      type: Array as () => IKanbanColumnOrdering[],
      default: null,
    },
    statuses: {
      type: Array as () => IStatusForSelection[],
      default: null,
    },
    members: {
      type: Array as () => IPersonBase[],
      default: null,
    },
    priorities: {
      type: Array as () => IPriorityLevelForSelection[],
      default: null,
    },
    workflowStatusTransitions: {
      type: Array as () => IWorkflowDetailStatusTransition[],
      default: null,
    },
    showUnsortedTasksOnTop: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isDragging: false,
      RouteAssistant,
      curSelectedStatusID: null,
      kanbanColumns: [] as Array<IHomeKanbanColumn>,
      newlyAddedTaskIDs: [] as Array<Number>,
    }
  },

  computed: {
    isLoading(): boolean {
      return this.kanbanColumns.length === 0
    },
  },

  watch: {
    tasks: {
      immediate: true,
      handler() {
        this.refreshColumns()
      },
    },
    statuses: {
      immediate: true,
      handler() {
        this.refreshColumns()
      },
    },
    showUnsortedTasksOnTop: {
      handler() {
        this.refreshColumns()
      },
    },
  },

  methods: {
    /* Renames the kanban columns such that 'droppableGroup' are all columns that the current
       card can drop to (via the curSelectedStatusID assignment on "choose") and 'noDropGroup' are
       all columns that it can't drop into, by inspecting the workflow status transitions. */
    itemGroupName(statusID: number): string {
      return this.droppableColumn(statusID) ? 'dropGroup' : 'noDropGroup'
    },
    droppableColumn(statusID: number): boolean {
      if (this.curSelectedStatusID === null) {
        return true
      }
      if (
        this.workflowStatusTransitions.find(
          w =>
            this.curSelectedStatusID === statusID ||
            (w.statusIDFrom === this.curSelectedStatusID &&
              w.statusIDTo === statusID),
        )
      ) {
        return true
      }
      return false
    },
    onChoosing(statusID: Number) {
      this.isDragging = true
      this.curSelectedStatusID = statusID
      this.$emit('dragging-started')
    },
    onUnchoosing() {
      this.isDragging = false
      this.curSelectedStatusID = null
      this.$emit('dragging-completed')
    },
    handleMovedItem(val: any, statusID: number) {
      const tasksForStatus = this.kanbanColumns.find(
        c => c.status.id === statusID,
      ).tasks

      /* Mark all tasks as manually ordered */
      tasksForStatus.forEach(t => (t.isManuallyOrdered = true))
      const idOrder = tasksForStatus.map(t => t.taskID)

      const taskID = val.added
        ? val.added.element.taskID
        : val.moved
          ? val.moved.element.taskID
          : null

      if (taskID) {
        this.$emit('moved-task', taskID, statusID, idOrder)
      }
    },
    refreshColumns() {
      this.kanbanColumns = new Array<IHomeKanbanColumn>()
      this.statuses.forEach(s => {
        this.kanbanColumns.push({
          status: s,
          tasks: this.orderTasksInColumn(s.id),
        })
      })
    },
    hasTasks(statusID: number): boolean {
      if (this.tasks.find(i => i.statusID === statusID)) {
        return true
      }
      return false
    },
    orderTasksInColumn(statusID: number): ITaskForKanbanWithManualOrder[] {
      const columnOrdering = this.columnOrderings.find(
        c => c.statusID === statusID,
      )

      if (this.showUnsortedTasksOnTop) {
        // (1) Sort by tasks created on descending (new cards at top)...
        const tasksInColumn = this.tasks
          .filter(i => i.statusID === statusID)
          .sort(
            (a, b) =>
              a.lastUpdatedEpochTime > b.lastUpdatedEpochTime ? -1 : 1,
          )
          .map(t => ({ ...t, isManuallyOrdered: false }))

        if (!columnOrdering || columnOrdering.listItemIDs.length === 0) {
          return tasksInColumn
        }

        // (2) Push cards found in column ordering...
        const result: ITaskForKanbanWithManualOrder[] = []

        columnOrdering.listItemIDs.forEach(c => {
          const task = tasksInColumn.find(t => c === t.taskID)
          if (task) {
            task.isManuallyOrdered = true
            result.push(task)
          }
        })

        // (3) Push the rest at the top...
        const newTasks = tasksInColumn.filter(
          t => !columnOrdering.listItemIDs.includes(t.taskID),
        )

        result.unshift(...newTasks)
        return result
      } else {
        // (1) Sort by tasks created on ascending (new cards at bottom)...
        const tasksInColumn = this.tasks
          .filter(i => i.statusID === statusID)
          .sort(
            (a, b) =>
              a.lastUpdatedEpochTime > b.lastUpdatedEpochTime ? 1 : -1,
          )
          .map(t => ({ ...t, isManuallyOrdered: false }))

        if (!columnOrdering || columnOrdering.listItemIDs.length === 0) {
          return tasksInColumn
        }

        // (2) Push cards found in column ordering first...
        const result: ITaskForKanbanWithManualOrder[] = []

        columnOrdering.listItemIDs.forEach(c => {
          const task = tasksInColumn.find(t => c === t.taskID)
          if (task) {
            task.isManuallyOrdered = true
            result.push(task)
          }
        })

        // (3) Push the rest at the bottom...
        const extras = tasksInColumn.filter(
          t => !columnOrdering.listItemIDs.includes(t.taskID),
        )

        result.push(...extras)
        return result
      }
    },
    getPriorityForItem(task: ITaskForKanbanWithManualOrder) {
      return this.priorities.find(p => p.id === task.priorityLevelID)
    },
    getAssigneeForItem(task: ITaskForKanbanWithManualOrder) {
      if (task.assigneeID) {
        return this.members.find(p => p.id === task.assigneeID)
      }
      return null
    },
    getMatchCount(kanbanColumn: IHomeKanbanColumn) {
      return kanbanColumn.tasks.filter(t => t.filterMatch).length
    },
    getCardID(taskID: Number): string {
      return `kanban-board-task-${taskID}`
    },
    isNewTask(taskID: Number): boolean {
      return this.newlyAddedTaskIDs.includes(taskID)
    },
    async scrollToNewTask(taskID: Number) {
      this.newlyAddedTaskIDs.push(taskID)
      await this.$nextTick()
      const card = document.getElementById(this.getCardID(taskID))
      card.scrollIntoView()
    },
    clearNewTasks() {
      this.newlyAddedTaskIDs = []
    },
    checkMove(evt: any): boolean {
      return (
        this.$permissions.isNotGuest ||
        evt.draggedContext.element.assigneeID ===
          this.$permissions.loggedInUser.id
      )
    },
  },
})
